import { useEffect, useMemo, useState } from 'react';
import type { PartialDeep } from 'type-fest';
import type { Filter } from 'contexts/preferences/listing-params/types';
import { COUNT_DEBOUNCE_MS } from './smart-search.constants';

/**
 * Asks the search API how many listings a filter patch would match, in the
 * given area or the current one. Resolves to null when superseded or fails.
 */
export type CountListingsForFilter = (
  filter: PartialDeep<Filter>,
  areaSlug?: string
) => Promise<number | null>;

export interface UseSmartSearchResultsCountOptions {
  /** The pending patch to count, as produced by `useSmartSearchState`. */
  filterPatch: PartialDeep<Filter>;
  /** Area the query would move to, when it names one. Counted there instead. */
  areaSlug?: string | null;
  /** Count for the filters already applied to the page. */
  appliedCount?: number;
  countListings?: CountListingsForFilter;
  debounceMs?: number;
}

/** The result of the newest settled count request, tagged with its patch. */
interface CountSnapshot {
  key: string;
  count: number;
}

const EMPTY_PATCH_KEY = '{}';

/**
 * Live listing count for a pending smart-search query.
 *
 * The count trails the query by a debounce and a round trip, so while a newer
 * count is in flight the previous one keeps showing. A slightly stale number
 * reads better on a button than a number that blanks out or snaps back to the
 * unfiltered total on every keystroke.
 *
 * With no patch and no area to move to, the query would not change the search
 * at all and the applied count is already the right answer. Naming an area is
 * a change on its own, though: "condos in Toronto" has to be counted in Toronto
 * even before a single filter is set.
 */
export function useSmartSearchResultsCount({
  filterPatch,
  areaSlug,
  appliedCount,
  countListings,
  debounceMs = COUNT_DEBOUNCE_MS,
}: UseSmartSearchResultsCountOptions): number | undefined {
  const [snapshot, setSnapshot] = useState<CountSnapshot | null>(null);

  const patchKey = useMemo(
    () => `${areaSlug ?? ''}:${JSON.stringify(filterPatch)}`,
    [areaSlug, filterPatch]
  );
  const isCountable = JSON.stringify(filterPatch) !== EMPTY_PATCH_KEY || Boolean(areaSlug);

  useEffect(() => {
    if (!countListings || !isCountable || snapshot?.key === patchKey) {
      return;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(() => {
      void countListings(filterPatch, areaSlug ?? undefined).then(count => {
        // A null count means superseded or failed; leave the last one showing.
        if (!isCancelled && count !== null) {
          setSnapshot({ key: patchKey, count });
        }
      });
    }, debounceMs);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [filterPatch, areaSlug, patchKey, isCountable, snapshot, countListings, debounceMs]);

  if (!isCountable) {
    return appliedCount;
  }
  return snapshot?.count ?? appliedCount;
}

export default useSmartSearchResultsCount;
